import { PreventAndRedirectCommands, RedirectResult, Router, RouterLocation } from "@vaadin/router";
import { LitElement, html, css, TemplateResult } from "lit";
import { customElement, property, query, state } from "lit/decorators.js";
import { Chart, registerables } from "chart.js";
Chart.register(...registerables);

import { container } from "tsyringe";
import { AuthService } from "../../services/auth.service";
import "../components/primary-button.element";
import { choose } from "lit/directives/choose.js";
import "../components/secondary-button.element";
import { BillingType } from "../../enums/billing-type";
import { ToasterService } from "se-shared/services/toaster.service";
import { UsageService } from "../../services/usage.service";
import { UserState } from "../../services/user.state";
import { SeDataGrid } from "../components/data-grid.element";
import "../components/data-grid.element";
import { ModalDialogService } from "../../services/modal-editor.service";

import { DataGridColumn } from "../components/data-grid-template";
import { SelectEditorElement } from "../editors/select-editor.element";
import { format, sub, startOfDay, endOfDay, startOfMonth, endOfMonth, startOfYear } from "date-fns";
import { formatInTimeZone } from 'date-fns-tz';
import { SortOrder } from "se-shared/enums/sort-order";
import { FormToggleEditorElement } from "../editors/form/form-toggle-editor.element";

@customElement("se-pipe-usage")
export class PipeUsage extends LitElement {
    private _usageService: UsageService;
    private _authService: AuthService;
    private _toasterService: ToasterService;
    private _modalService: ModalDialogService;
    private _pageIndex = 1;
    private _recordsPerPage = 10;
    private _totalRecordCount: number;
    private _sortColumn = "date";
    private _sortOrder = 1;
    private _columns: DataGridColumn[] = [];
    private _pipeId?: number;
    private _pipeName?: string;
    private _userState: UserState;
    private _isViewedFromAdmin = false;
    private _viewingOrgId?: number;

    // UI constants
    private RUN_USAGE = "Server Time";
    private EXPORT_DATA_BANDWIDTH = "Export GB";
    private INPUT_COUNT = "Agent Inputs";
    private PROXY_USAGE = "Proxy Data";
    private EXPORT_DATA_CPM = "Export CPM"

    @state() private _tableData: any[] = [];
    @state() private _usageDateRangeValue = "1";
    @state() private _usageTimeUnit = "day";
    @state() private _usageTypeCosts: any[] = [];
    @state() private timezoneIsShowUtc = true;

    @query("se-data-grid") private _dataGrid: SeDataGrid;
    @query("#chartCanvas") private _chartCanvas: HTMLCanvasElement;
    @query("#usageDateRange") private _usageDateRange: SelectEditorElement;
    @query("#timezoneToggle") private _timezoneToggle: FormToggleEditorElement;

    @property({ type: Date }) startDate = startOfMonth(new Date());
    @property({ type: Date }) endDate = endOfDay(new Date());
    @property({ type: Array }) datasets: any = [];
    @property({ type: Array }) labels: string[] = [];
    @property({ type: Number }) totalCost = 0.0;

    private chart?: Chart;

    private _usageTypeColumns: DataGridColumn[] = [];
    private _usageTypeSortColumn = "type";
    private _usageTypeSortOrder = 0;

    constructor() {
        super();
        this._authService = container.resolve(AuthService);
        this._usageService = container.resolve(UsageService);
        this._toasterService = container.resolve(ToasterService);
        this._modalService = container.resolve(ModalDialogService);
        this._userState = container.resolve(UserState);
    }

    connectedCallback() {
        super.connectedCallback();
        if (this._isViewedFromAdmin) this._userState.selectedSpaceId = -7;
        else this._userState.selectedSpaceId = -6;
        this._userState.selectedLabelId = -1;
        this._userState.selectedSpaceOrLabelChanged.triggerVoid();

    }
    disconnectedCallback() {
        super.disconnectedCallback();
    }

    public onBeforeEnter(location: RouterLocation, commands: PreventAndRedirectCommands): Promise<unknown> | RedirectResult | undefined {
        if (!this._authService.isLoggedIn) {
            return commands.redirect("/login");
        }
        
        // Check if viewed from admin context
        const pathParts = location.pathname.split('/');
        this._isViewedFromAdmin = pathParts.includes("admin");
        
        this.pageChange(location);
        if (location.params.pipeId) {
            this._pipeId = parseInt(location.params.pipeId.valueOf() as string);
        }
        if (location.params.orgId) {
            this._viewingOrgId = parseInt(location.params.orgId.valueOf() as string);
        }
    }

    pageChange(location: RouterLocation) {
        const message = {
            type: "pageChange",
            payload: { location: location.route },
        };
        window.postMessage(message, "*");
    }


    /**
     * Load the data for the detail table of run usage records
     */
    private async loadTableData() {
        // get pipe metadata
        //const pipeData = await this._pipeService.api.getPipeDataAsync(this._pipeId);
        //if (pipeData.isOk) {
        //    this._pipeName = pipeData.value.name;
        //}
        //else {
        //    this._toasterService.showUnexpectedError(pipeData.err.message);
        //}

        const orgId = this._isViewedFromAdmin ? this._viewingOrgId : this._authService.user.organizationId;

        const usageRequest = {
            orgId,
            pipeId: this._pipeId,
            timeUnit: this._usageTimeUnit,
            startDate: this.formatUTC(this.startDate),
            endDate: this.formatUTC(this.endDate),
            pageIndex: this._pageIndex,
            recordsPerPage: this._recordsPerPage,
            sortColumn: this._sortColumn,
            sortOrder: this._sortOrder,
        };
        const result = await this._usageService.api.getOrgPipeUsageAsync(usageRequest);
        if (result.isOk) {
            this._tableData = result.value.pipesUsage;
            this._totalRecordCount = result.value.totalRecordCount;
            this.calculateDistribution();
        } else {
            this._toasterService.showUnexpectedError(result.err.message);
        }
    }

    /**
     * Update the distribution of the data based on the total cost
     */
    private calculateDistribution() {
        if (this._tableData == null || this.totalCost == null) {
            return;
        }

        this._tableData.forEach((item) => {
            // To calculate the distribution of each individual Pipe as a percentage of the overall total price
            item.distribution = (item.cost / this.totalCost) * 100;
        });

        this.requestUpdate();
    }

    /**
     * Format the date to UTC in the format yyyy-mm-dd
     *
     * @param date
     * @returns
     */
    private formatUTC(date: Date): string {
        const month = date.getUTCMonth() + 1; // Gets the month, zero-indexed, so add 1
        const day = date.getUTCDate(); // Gets the day of the month according to universal time

        // Pad the month and day with a zero if they are less than 10
        const formattedMonth = month < 10 ? "0" + month : month;
        const formattedDay = day < 10 ? "0" + day : day;

        return date.getUTCFullYear() + "-" + formattedMonth + "-" + formattedDay;
    }

    async loadGraphData() {
        try {
            const response = await this._usageService.api.getOrgPipeRunsUsageAsync(
                this._pipeId,
                this._usageTimeUnit,
                format(this.startDate, "yyyy-MM-dd"),
                format(this.endDate, "yyyy-MM-dd")
            );

            if (response.isOk) {
                const data = await response.value;
                if (data.labels !== null && data.labels.length > 0) {
                    this.labels = data.labels;
                    this.datasets = data.dataSets;
                    this.totalCost = data.totalCost;
                    this._pipeName = data.configName;
                } else {
                    this.labels = [];
                    this.datasets = data.dataSets;
                    this.totalCost = 0;
                }

                // calculate usage type totals
                this.calculateUsageTypeTotals();

                //force the grid to render again
                this._columns = null;
                this.calculateDistribution();
            } else {
                this._toasterService.showUnexpectedError(`Usage API request failed with status: ${response.value}`);
            }
        } catch (error) {
            console.error("Error fetching data from API:", error);
        }
    }

    private async loadChart() {
        if (this._chartCanvas.getAttribute("prepared") == "true") {
            return;
        }
        this._chartCanvas.setAttribute("prepared", "true");
        const ctx = this._chartCanvas.getContext("2d");

        // Chart data
        const data = {
            labels: this.labels,
            datasets: this.getDataSets(),
        };

        // Chart options
        const options = {
            maintainAspectRatio: false,
            responsive: true,
            scales: {
                x: {
                    stacked: true,
                },
                y: {
                    stacked: true,
                    beginAtZero: true,
                    title: {
                        display: true,
                        text: "Cost",
                        color: "#1a1f4b", //#4e4ef0
                        font: {
                            family: "Helvetica",
                            size: 16,
                            weight: "bold",
                        },
                    },
                },
            },
            plugins: {
                legend: {
                    display: false,
                },
                tooltip: {
                    callbacks: {
                        label: (context) => {
                            const datasetLabel = context.dataset.label; // Get the label name
                            const value = context.parsed.y.toFixed(2); // Format the data to 6 decimal places
                            return `${datasetLabel}: $${value}`; // Combine label name and "$" cost value in the tooltip label
                        },
                    },
                },
            },
        };

        // Create Chart.js instance
        this.chart = new Chart(ctx, {
            type: "bar",
            data: data,
            options: options,
        });
    }
    private setCookie(name, value, minutes) {
        const now = new Date();
        now.setTime(now.getTime() + minutes * 60 * 1000);
        const expires = "expires=" + now.toUTCString();
        document.cookie = name + "=" + value + ";" + expires + ";path=/";
    }
    private getCookie(name) {
        const nameEQ = name + "=";
        const ca = document.cookie.split(";");
        for (let i = 0; i < ca.length; i++) {
            let c = ca[i];
            while (c.charAt(0) == " ") c = c.substring(1, c.length);
            if (c.indexOf(nameEQ) == 0) return c.substring(nameEQ.length, c.length);
        }
        return null;
    }
    firstUpdated() {
        this._usageDateRangeValue = this.getCookie("usageDateRangeValue") || "1d";
        this.timezoneIsShowUtc = this.getCookie("timezoneIsShowUtc") === "true";
        
        if (!this._isViewedFromAdmin) {
            this.classList.add('normal-padding');
        } else {
            this.classList.remove('normal-padding');
        }
        
        this.updateDateRange();
        this.loadChart();
    }

    updated(changedProperties: Map<string | number | symbol, unknown>): void {
        super.updated(changedProperties);
        if (changedProperties.has("labels")) {
            this.updateChart();
        }
        
        if (!this._isViewedFromAdmin) {
            this.classList.add('normal-padding');
        } else {
            this.classList.remove('normal-padding');
        }
    }

    private updateChart(): void {
        if (this.chart) {
            this.chart.data.labels = this.labels;
            this.chart.data.datasets = this.getDataSets();
            this.chart.update();
        }
    }

    private getDataSets() {
        const datasets = [];

        this.datasets.forEach((item) => {
            if (item.label == "RunUsage") {
                const dataset = {
                    label: this.RUN_USAGE,
                    data: item.data,
                    backgroundColor: ["#3F8CD9"],
                    borderColor: ["#3F8CD9"],
                    borderWidth: 1,
                };
                datasets.push(dataset);
            }

            if (item.label == "ExportDataBandwidthUsage") {
                const dataset = {
                    label: this.EXPORT_DATA_BANDWIDTH,
                    data: item.data,
                    backgroundColor: ["#93bfea"],
                    borderColor: ["#93bfea"],
                    borderWidth: 1,
                };
                datasets.push(dataset);
            }

            if (item.label == "ExportDataCpmUsage") {
                const dataset = {
                    label: this.EXPORT_DATA_CPM,
                    data: item.data,
                    backgroundColor: ["#884CC9"],
                    borderColor: ["#884CC9"],
                    borderWidth: 1,
                };
                datasets.push(dataset);
            }

            if (item.label == "InputCount") {
                const dataset = {
                    label: this.INPUT_COUNT,
                    data: item.data,
                    backgroundColor: ["#4E4EF0"],
                    borderColor: ["#4E4EF0"],
                    borderWidth: 1,
                };
                datasets.push(dataset);
            }

            if (item.label == "ProxyUsage") {
                const dataset = {
                    label: this.PROXY_USAGE,
                    data: item.data,
                    backgroundColor: ["#FC833F"],
                    borderColor: ["#FC833F"],
                    borderWidth: 1,
                };
                datasets.push(dataset);
            }
        });

        return datasets;
    }

    private updateDateRange(loadData = true) {
        //we may have to change the end date logic for Months filter once we have proper data.
        const today = new Date();
        switch (this._usageDateRangeValue) {
            case "1d":
                this.startDate = startOfMonth(today);
                this.endDate = endOfDay(today);
                this._usageTimeUnit = "day";
                break;
            case "1m":
                this.startDate = startOfMonth(sub(today, { months: 1 }));
                this.endDate = endOfMonth(sub(today, { months: 1 }));
                this._usageTimeUnit = "day";
                break;
            case "3m":
                this.startDate = startOfMonth(sub(today, { months: 3 }));
                this.endDate = endOfDay(today); //endOfMonth(sub(today, { months: 1 }));
                this._usageTimeUnit = "day";
                break;
            case "6m":
                this.startDate = startOfMonth(sub(today, { months: 6 }));
                this.endDate = endOfDay(today); //endOfMonth(sub(today, { months: 1 }));
                this._usageTimeUnit = "day";
                break;
            case "1y":
                this.startDate = startOfYear(sub(today, { years: 1 }));
                this.endDate = endOfDay(today);
                this._usageTimeUnit = "year";
                break;
            default:
                // Default to the current date range
                this.startDate = startOfDay(today);
                this.endDate = endOfDay(today);
                break;
        }

        this._totalRecordCount = 0;
        this._tableData = [];
        this.loadGraphData();
        if (loadData) {
            this.loadTableData();
        }
    }

    private handleDateChange() {
        this._usageDateRangeValue = this._usageDateRange.liveValue;
        this.setCookie("usageDateRangeValue", this._usageDateRange.liveValue, 15);
        this.updateDateRange();
    }

    private sortDataGrid(evt: CustomEvent) {
        evt.stopPropagation();
        const sortColumn = evt.detail.sortColumn;
        const sortOrder = evt.detail.sortOrder;

        this._sortColumn = sortColumn;
        this._sortOrder = sortOrder;

        this.loadTableData();
    }
    private onPageChanged(evt: CustomEvent) {
        evt.stopPropagation();
        this._pageIndex = evt.detail.pageIndex;
        this._dataGrid.pageIndex = this._pageIndex;

        this.loadTableData();
    }

    private getDistribution(value: number): TemplateResult {
        return html` 
            <div style="display: flex; justify-content: flex-start;">
                <div style="display: flex; min-width: 100px; height: 16px; justify-content: flex-start; align-items: center; gap: 10px; border-radius: 8px; background: var(--color-blue-50, #cee1f4); overflow: hidden; flex-shrink: 1;">
                    <span style="height: 100%; width: ${value}%; border-radius: 8px; background-color: var(--color-status-blue);"></span>
                </div>
            </div>`;
    }

    private calculateDuration(startTime: Date, endTime: Date): string {
        if (!startTime || !endTime) return "N/A";
        
        const start = new Date(startTime);
        const end = new Date(endTime);
        
        const diff = end.getTime() - start.getTime();
        
        const seconds = Math.floor(diff / 1000);
        
        if (seconds < 60) {
            return `${seconds} sec`;
        } else if (seconds < 3600) {
            const minutes = Math.floor(seconds / 60);
            const remainingSeconds = seconds % 60;
            return `${minutes}m ${remainingSeconds}s`;
        } else {
            const hours = Math.floor(seconds / 3600);
            const remainingMinutes = Math.floor((seconds % 3600) / 60);
            return `${hours}h ${remainingMinutes}m`;
        }
    }

    /**
     * Get the columns for the data grid
     * @returns
     */
    private getColumns() {
        if (this._columns == null || this._columns.length == 0) {
            //expand decimals places if ALL of the numbers are very small
            const decimalPlaces = this.totalCost >= 0.01 ? 2 : 3;

            this._columns = [
                {
                    field: "date",
                    title: this.timezoneIsShowUtc ? "Date (UTC)" : "Date (Local)",
                    sortable: true,
                    align: "left",
                    template: (row) => {
                        const utcDate = new Date(row.created);
                        const linkUrl = row.runId && row.configId ? 
                            `/space/${row.spaceId||0}/config/${row.configId}/details/${row.runType == "run" ? "run-details" : "run-history-details"}/run/${row.runId}` : 
                            '';
                            //link color #0000ee to avoid :visited color
                        return html`<a href="${linkUrl}" 
                           style="text-decoration: none; color: #0000ee;" 
                           @click=${(e) => {
                               if (linkUrl) {
                                   e.preventDefault();
                                   this.navigateToRunDetails(row);
                               }
                           }}
                           @mouseover=${(e) => e.target.style.textDecoration = "underline"} 
                           @mouseout=${(e) => e.target.style.textDecoration = "none"}>
                           ${this.timezoneIsShowUtc ? formatInTimeZone(utcDate, "UTC", "M/d/yyyy h:mm:ss a") : html`${new Date(utcDate).toLocaleDateString()} ${new Date(utcDate).toLocaleTimeString()}`}
                        </a>`;
                    },
                },
                {
                    field: "duration",
                    title: "Duration",
                    sortable: false,
                    template: (row) => {
                        return html`${this.calculateDuration(row.startTime, row.endTime)}`;
                    },
                },
                {
                    field: "details",
                    title: "Details",
                    sortable: false,
                    template: (row) => html`
                        <a href="javascript:;" 
                           style="text-decoration: none;" 
                           @click=${() => this.showDetailsModal(row)} 
                           @mouseover=${(e) => e.target.style.textDecoration = "underline"} 
                           @mouseout=${(e) => e.target.style.textDecoration = "none"}>
                           Details
                        </a>
                    `,
                },
                {
                    field: "cost",
                    title: "Cost",
                    sortable: true,
                    align: "right",
                    cellStyle: { textAlign: "right", width: "150px" },
                    template: (row) =>
                        html`<span class="numbers" style="font-feature-settings: var(--font-feature-numbers);"
                            >${"$" + row.cost.toFixed(decimalPlaces)}</span
                        >`,
                },
                {
                    field: "distribution",
                    title: "Distribution",
                    align: "center",
                    cellStyle: { textAlign: "center", width: "300px" },
                    template: (row) => html`${this.getDistribution(row.distribution)}`,
                },
            ];
        }

        return this._columns;
    }

    private calculateUsageTypeTotals() {
        if (!this.datasets || this.datasets.length === 0 || this.totalCost === 0) {
            this._usageTypeCosts = [];
            return;
        }

        this._usageTypeCosts = [];
        this.datasets.forEach(dataset => {
            let usageType = "";
            switch (dataset.label) {
                case "RunUsage":
                    usageType = this.RUN_USAGE;
                    break;
                case "ExportDataBandwidthUsage":
                    usageType = this.EXPORT_DATA_BANDWIDTH;
                    break;
                case "ExportDataCpmUsage":
                    usageType = this.EXPORT_DATA_CPM;
                    break;
                case "InputCount":
                    usageType = this.INPUT_COUNT;
                    break;
                case "ProxyUsage":
                    usageType = this.PROXY_USAGE;
                    break;
            }

            if (usageType && dataset.totalCost) {
                const distribution = (dataset.totalCost / this.totalCost) * 100;
                this._usageTypeCosts.push({
                    type: usageType,
                    cost: dataset.totalCost,
                    distribution: distribution
                });
            }
        });

        // sort by type
        this._usageTypeCosts.sort((a, b) => {
            return this._usageTypeSortOrder === 0 ? 
                String(a[this._usageTypeSortColumn]).localeCompare(String(b[this._usageTypeSortColumn])) : 
                String(b[this._usageTypeSortColumn]).localeCompare(String(a[this._usageTypeSortColumn]));
        });
    }

    private getColorForUsageType(usageType: string): string {
        switch (usageType) {
            case this.RUN_USAGE:
                return "var(--color-status-blue)";
            case this.EXPORT_DATA_BANDWIDTH:
                return "var(--color-blue-50)";
            case this.EXPORT_DATA_CPM:
                return "#884CC9";
            case this.INPUT_COUNT:
                return "var(--color-purple)";
            case this.PROXY_USAGE:
                return "var(--color-status-orange)";
            default:
                return "#ccc";
        }
    }

    private getUsageTypeColumns() {
        if (this._usageTypeColumns == null || this._usageTypeColumns.length == 0) {
            this._usageTypeColumns = [
                {
                    field: "type",
                    title: "Type",
                    sortable: true,
                    template: (row) => {
                        const color = this.getColorForUsageType(row.type);
                        return html`
                            <div style="display: flex; align-items: center; gap: 10px;">
                                <div style="border-radius: 50%; height: 15px; width: 15px; background-color: ${color};"></div>
                                <span>${row.type}</span>
                            </div>
                        `;
                    }
                },
                {
                    field: "cost",
                    title: "Cost",
                    align: "right",
                    sortable: true,
                    cellStyle: { textAlign: "right", width: "150px" },
                    template: (row) =>
                        html`<span style="font-feature-settings: var(--font-feature-numbers);">${"$" + row.cost.toFixed(2)}</span>`
                },
                {
                    field: "distribution",
                    title: "Distribution",
                    sortable: true,
                    align: "center",
                    cellStyle: { textAlign: "center", width: "300px" },
                    template: (row) => html`${this.getDistribution(row.distribution)}`
                }
            ];
        }
        return this._usageTypeColumns;
    }

    private sortUsageTypeDataGrid(evt: CustomEvent) {
        evt.stopPropagation();
        this._usageTypeSortColumn = evt.detail.sortColumn;
        //this._usageTypeSortOrder = evt.detail.sortOrder;
        this._usageTypeSortOrder = evt.detail.sortOrder == SortOrder.Ascending ? 1 : 0;

        this._usageTypeCosts.sort((a, b) => {
            const aValue = a[this._usageTypeSortColumn];
            const bValue = b[this._usageTypeSortColumn];
            
            if (typeof aValue === 'number') {
                return this._usageTypeSortOrder === 0 ? aValue - bValue : bValue - aValue;
            } else {
                return this._usageTypeSortOrder === 0 ? 
                    String(aValue).localeCompare(String(bValue)) : 
                    String(bValue).localeCompare(String(aValue));
            }
        });

        this.requestUpdate();
    }
    private navigateToRunDetails(row: any) {
        if (row.runId && row.configId) {
            this._userState.selectedSpaceId = row.spaceId||0;
            this._userState.selectedLabelId = -1;
            Router.go(`/space/${row.spaceId||0}/config/${row.configId}/details/${row.runType == "run" ? "run-details" : "run-history-details"}/run/${row.runId}`);
        }
    }

    private async showDetailsModal(row: any) {
        if (!row.runId) {
            this._toasterService.showWarning("No run ID available for this record");
            return;
        }
        
        try {
            // new separate api for getting Run Usage Types
            const response = await this._usageService.api.getRunUsageTypesAsync(row.runId);
            
            if (!response.isOk) {
                this._toasterService.showError("Failed to load usage details: " + response.err.message);
                return;
            }
            
            const usageTypes = response.value.pipesUsage as Array<{billingType: BillingType, cost: number}>;
            
            const usageDetailsObject = {};
            
            if (usageTypes && usageTypes.length > 0) {
                usageTypes.forEach(usage => {
                    usageDetailsObject[this.getBillingTypeLabel(usage.billingType)] = `$${usage.cost.toFixed(2)}`;
                });
            } else {
                usageDetailsObject["No usage types found"] = "";
            }
            
            await this._modalService.openObjectInfoDialogAsync({
                title: "Usage Details",
                anyObject: usageDetailsObject
            });
        } catch (error) {
            console.error("Error showing usage details:", error);
            this._toasterService.showError("Failed to load usage details");
        }
    }

    private getBillingTypeLabel(billingType: BillingType): string {
        switch (billingType) {
            case BillingType.RunUsage:
                return this.RUN_USAGE;
            case BillingType.ExportDataBandwidthUsage:
                return this.EXPORT_DATA_BANDWIDTH;
            case BillingType.ExportedData:
                return this.EXPORT_DATA_CPM;
            case BillingType.InputCount:
                return this.INPUT_COUNT;
            case BillingType.TrafficData:
                return this.PROXY_USAGE;
            default:
                return `Type ${billingType}`;
        }
    }

    private toggleTimezone() {
        this.timezoneIsShowUtc = this._timezoneToggle.liveValue;
        this.setCookie("timezoneIsShowUtc", this.timezoneIsShowUtc.toString(), 60 * 24 * 30);
        
        this._columns = null;
        this.requestUpdate();
    }

    render() {
        const displayCost = this.totalCost < 0.01 && this.totalCost != 0 ? this.totalCost.toFixed(4) : this.totalCost.toFixed(2);

        return html`
                ${!this._isViewedFromAdmin ? html`
                    <div class="breadcrumbs">
                        <a disabled>Manage</a>
                        <span>/</span>
                        <a href="/usage">Usage</a>
                        <span>/</span>
                        <a disabled>${this._pipeName}</a>
                    </div>
                    <div class="title-header"><h1>${this._pipeName} Usage</h1></div>
                ` : html``}
                <div class="container-content scroll-container">
                    <div class="table-header">
                        <div class="left-header">
                            <se-select-editor class="inputEditor" id="usageDateRange" .value="${this._usageDateRangeValue}" width="100%" name="usageDateRange" label="" labelPosition="top"
                                    @valueChanged=${this.handleDateChange}
                                    .options=${[
                                        { id: "1d", name: "This Month" },
                                        { id: "1m", name: "Last Month" },
                                        { id: "3m", name: "3 Months" },
                                        { id: "6m", name: "6 Months" },
                                        { id: "1y", name: "1 Year" },
                                    ]}>
                            </se-select-editor>
                        </div>
                        <div class="right-header">
                            Total: <span class="h3"><strong>$${displayCost} </strong></span>
                        </div>
                    </div>
                    <div class="usage-list">
                        <div style="position: relative; height:30vh; width: calc(100% - 240px);">
                            <canvas id="chartCanvas"></canvas>
                        </div>
                        <div class="labelsFilters">
                            <div class="market-item-div"><div class="usage-color" style="background-color: #4E4EF0"></div><span>Agent Inputs</span></div> 
                            <div class="market-item-div"><div class="usage-color" style="background-color: #884CC9"></div><span>Export CPM</span></div>
                            <div class="market-item-div"><div class="usage-color" style="background-color: var(--color-blue-50)"></div><span>Export GB</span></div>
                            <div class="market-item-div"><div class="usage-color" style="background-color: var(--color-status-orange)"></div><span>Proxy Data</span></div>
                            <div class="market-item-div"><div class="usage-color" style="background-color: var(--color-status-blue)"></div><span>Server Time</span></div> 
                        </div>
                    </div>
                    <div class="footnote">* For billing accuracy all dates and times are in UTC</div>
                </div>
                
                <div class="container-content usage-type-card">
                    <h2 style="padding: 0 5px;" class="table-header">Usage Totals</h2>
                    <se-data-grid 
                        .rows=${this._usageTypeCosts} 
                        .columns=${this.getUsageTypeColumns()}
                        .sortColumn=${this._usageTypeSortColumn}
                        .sortOrder=${this._usageTypeSortOrder === 1 ? SortOrder.Descending : SortOrder.Ascending}
                        @sortdata=${this.sortUsageTypeDataGrid}
                        placeholder="No usage data available"
                        .syncWithUrl=${false}
                    </se-data-grid>
                </div>
                
                <div class="container-content" style="flex: 1; display: flex; flex-direction: column;">
                        <div style="padding: 0 5px; display: flex; justify-content: space-between; align-items: center;" class="table-header">
                            <h2>Runs</h2>
                            <div class="timezone-toggle">
                                <se-form-toggle-editor
                                    id="timezoneToggle"
                                    .value=${this.timezoneIsShowUtc}
                                    @editorChanged=${this.toggleTimezone}
                                    .hideRow=${true}
                                    label="Show UTC Times"
                                ></se-form-toggle-editor>
                            </div>
                        </div>
                        <div class="table-list">
                            <se-data-grid class="grid" .rows=${this._tableData} .recordsPerPage=${this._recordsPerPage} .pageIndex=${this._pageIndex} .columns=${this.getColumns()} @sortdata=${this.sortDataGrid} placeholder="No usage available."></se-data-grid>
                        </div>
                        <se-pagination .dataType=${"Runs"} .recordsPerPage=${this._recordsPerPage} .recordCount=${this._totalRecordCount} @pagechanged=${this.onPageChanged}></se-pagination>
                    </div>
                </div>
        `;
    }

    static styles = css`
        :host {
            display: block;
            box-sizing: border-box;
            font: var(--font);
            height: 100%;
        }
        :host(.normal-padding) {
            padding: 15px;
        }
        .body {
            height: 100%;
            display: flex;
            flex-direction: column;
            gap: 5px;
        }
        .header {
            margin-left: 5px;
            display: flex;
            align-items: end;
            justify-content: space-between;
            overflow: hidden;
            padding-right: 5px;
            margin-right: -5px;
            padding-bottom: 5px;
            margin-bottom: -5px;
        }
        .left-header {
            display: flex;
            align-items: center;
            gap: 5px;
        }
        .grid {
            flex: 1;
        }
        .checkbox {
            width: 1rem;
            height: 1rem;
        }
        input[type="checkbox"]:checked {
            background-color: var(--color-secondary);
        }
        .label {
            background-color: dimgray;
            border-radius: 3px 3px;
            font: var(--font-smaller);
        }
        .container-header {
            justify-content: space-between;
        }
        .container-content.scroll-container {
            margin-bottom: 20px;
         }
        .usage-item-label::before {
            content: "";
            position: absolute;
            top: -5px;
            left: -10px;
            height: 30px;
            width: calc(100% + 20px);
            border-radius: 15px;
            z-index: -1;
        }
        .transparent-bg {
            background-color: #ffffff00;
        }
        .labelsFilters {
            display: flex;
            gap: 8px;
            flex-direction: column;
            place-content: center flex-start;
            align-items: center;
            min-width: 220px;
            height: 100%;
        }
        .market-item-div {
            padding: 12px;
            width: 200px;
            text-align: center;
            border-radius: 9px;
            border: 1px solid var(--color-gray-2, #d0d3dc);
            display: flex;
            gap: 12px;
        }
        .usage-color {
            border-radius: 50%;
            height: 15px;
            width: 15px;
            align-self: center;
        }
        .usage-list {
            display: flex;
            gap: 20px;
            justify-content: space-between;
        }
        .right-header {
            display: flex;
            flex-direction: column;
            align-items: flex-end;
        }
        .right-header .numbers {
            font-feature-settings: var(--font-feature-numbers);
            font-weight: bold;
        }
        .table-header {
            width: 100%;
            display: flex;
            justify-content: space-between;
            flex-direction: row;
            align-self: flex-end;
            padding: 25px 30px;
            box-sizing: border-box;
        }
        .container-content {
            background-color: white;
            box-sizing: border-box;
            border-radius: 5px;
            margin-bottom: 5px;
            box-shadow: rgba(0, 0, 0, 0.1) 0px 1px 5px 0px;
            padding: 10px;
        }
        .table-list {
            flex: 1;
            overflow: auto;
            display: flex;
            flex-direction: column;
        }
        .scroll-container {
            min-height: 0;
            overflow: hidden;
            padding: 10px 10px 20px 10px;
        }
        .footnote
        {
          font-size: 80%;
          color: #999;
          margin: 15px 10px;
        }
        h3 {
            margin: 15px 0 10px 0;
            font-weight: 500;
            padding-left: 10px;
        }
        .usage-type-card {
            padding: 10px;
            margin-bottom: 20px;
        }
        .no-data {
            text-align: center;
            color: var(--color-text-secondary);
            padding: 15px;
        }
        .breadcrumbs {
            display: flex;
            flex-direction: row;
            gap: 10px;
            font: var(--font-smaller);
            flex-wrap: wrap;
            margin-bottom: 10px;
        }
        .breadcrumbs a {
            color: var(--color-primary);
            text-decoration: none;
        }
        .breadcrumbs a:hover:not([disabled]) {
            text-decoration: underline;
        }
        .breadcrumbs a[disabled] {
            pointer-events: none;
        }
        .title-header {
            padding: 15px 0px;
            display: flex;
            align-items: center;
            justify-content: space-between;
        }
        h1 {
            margin: 0px;
            font-weight: 600;
        }
        .timezone-toggle {
            display: flex;
            align-items: center;
            gap: 8px;
            margin-right: 60px;
        }
    `;
}
